"use client"

import { Table } from "@tanstack/react-table"
import { X } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { DataTableViewOptions } from "./data-table-view-options"

import { DataTableFacetedFilter } from "@/views/users/components/data-table-faceted-filter"
import { FilterConfig } from "./types"
import { useTranslation } from "react-i18next"
import { debounceAsync } from "@/utils/debounce"

interface DataTableToolbarProps<TData> {
    table: Table<TData>
    filterConfig?: FilterConfig
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    params?: any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setParams?: React.Dispatch<React.SetStateAction<any>>
}

export function DataTableToolbar<TData>({
    table,
    filterConfig,
    params,
    setParams,
}: DataTableToolbarProps<TData>) {
    const { t } = useTranslation()
    const isFiltered = table.getState().columnFilters.length > 0

    const handleSearchChange = debounceAsync(async (id: string, value: string) => {
        if (setParams && params) {
            setParams({ ...params, [id]: value === "" ? null : value })
        }
    }, 500)

    const handleFacetChange = (id: string, values: string[]) => {
        if (setParams && params) {
            // For multi-select, we might want to pass array or null if empty
            // Adjust logic based on your backend requirements
            setParams({ ...params, [id]: values.length > 0 ? values : null })
        }
    }

    return (
        <div className="flex items-center justify-between">
            <div className="flex flex-1 flex-col-reverse items-start gap-y-2 gap-x-2 sm:flex-row sm:items-center sm:space-x-2">
                {filterConfig?.fields.map((field) => {
                    if (field.type === 'search') {
                        return (
                            <Input
                                key={field.id}
                                placeholder={field.placeholder || t('filter')}
                                defaultValue={params?.[field.id] || ""}
                                onChange={(event) => handleSearchChange(field.id, event.target.value)}
                                className="h-8 w-[150px] lg:w-[250px]"
                            />
                        )
                    }
                    if (field.type === 'select' && field.options) {
                        return (
                            <DataTableFacetedFilter
                                key={field.id}
                                title={field.label}
                                options={field.options}
                                defaultSelected={params?.[field.id] || []}
                                onFacetChange={(values) => handleFacetChange(field.id, values)}
                            />
                        )
                    }
                    return null
                })}

                {isFiltered && (
                    <Button
                        variant="ghost"
                        onClick={() => table.resetColumnFilters()}
                        className="h-8 px-2 lg:px-3"
                    >
                        {t('reset')}
                        <X className="ml-2 h-4 w-4" />
                    </Button>
                )}
            </div>
            <DataTableViewOptions table={table} />
        </div>
    )
}
